/**
 * forgot-password.tsx — Form untuk request link reset password.
 * Lives at `/forgot-password`. Tidak butuh session.
 */
import { useState, type ReactNode, type FormEvent } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { forgotPasswordMutation } from '../lib/api/mutations.js';
import { PageHeader } from '../components/ui/PageHeader.js';

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage(): ReactNode {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const forgotMut = useMutation(forgotPasswordMutation());

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!email.includes('@')) {
      setError('Email tidak valid.');
      return;
    }
    try {
      await forgotMut.mutateAsync({ email: email.trim().toLowerCase() });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal mengirim.');
    }
  }

  if (done) {
    return (
      <main className="page-shell" data-testid="forgot-password-page">
        <PageHeader
          eyebrow="Reset password"
          title="Cek email Anda"
          description="Jika email terdaftar, kami sudah mengirim link reset. Cek inbox (atau folder spam)."
        />
        <p>
          <Link to="/login" className="button button-primary" search={{ error: undefined, redirect: undefined }}>
            Kembali ke halaman masuk
          </Link>
        </p>
      </main>
    );
  }

  return (
    <main className="page-shell" data-testid="forgot-password-page">
      <PageHeader
        eyebrow="Reset password"
        title="Lupa password?"
        description="Masukkan email kamu. Kami akan mengirim link untuk reset."
      />
      <form className="stack" onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="forgot-email">Email</label>
          <input
            id="forgot-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            disabled={forgotMut.isPending}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="forgot-email"
          />
        </div>
        <button
          type="submit"
          className="button button-primary"
          disabled={forgotMut.isPending}
          data-testid="forgot-submit"
        >
          {forgotMut.isPending ? 'Mengirim…' : 'Kirim link reset'}
        </button>
        {error ? (
          <p className="form-message is-error" role="alert">
            {error}
          </p>
        ) : null}
        <p className="form-message form-message-muted">
          <Link to="/login" search={{ error: undefined, redirect: undefined }}>Kembali ke halaman masuk</Link>
        </p>
      </form>
    </main>
  );
}
